/**
 * SaveTemplateDialog — centred modal to save the current scene as a reusable
 * template. Calls onSave({ name, description, category }); Save is disabled
 * until a non-empty name is entered.
 */
import React, { useState, useEffect } from 'react';
import { X, LayoutTemplate } from 'lucide-react';
import { T } from '../../../components/em/tokens';
import { TEMPLATE_CATEGORIES } from '../pgConstants';

const fieldLabel = { display: 'block', font: `500 12px/1.3 ${T.font}`, color: T.fg3, marginBottom: 6 };

const inputStyle = {
  width: '100%', padding: '9px 12px', boxSizing: 'border-box',
  border: `1px solid ${T.border}`, borderRadius: T.rMd,
  background: T.bgCard, color: T.fg1, font: `400 13px/1.3 ${T.font}`, outline: 'none',
};

export default function SaveTemplateDialog({ open, onClose, onSave }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState(TEMPLATE_CATEGORIES[0]);

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
      setCategory(TEMPLATE_CATEGORIES[0]);
    }
  }, [open]);

  if (!open) return null;

  const canSave = !!name.trim();
  const submit = () => {
    if (!canSave) return;
    onSave({ name: name.trim(), description: description.trim(), category });
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Save as template"
        style={{ width: 420, maxWidth: '100%', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: T.rXl, boxShadow: T.shadowMd, overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: `1px solid ${T.divider}` }}>
          <span style={{ font: `700 16px/1.2 ${T.font}`, color: T.fg1 }}>Save as template</span>
          <button type="button" onClick={onClose} title="Close" aria-label="Close" style={{ width: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: 0, background: 'transparent', borderRadius: T.rSm, cursor: 'pointer', color: T.fg5 }}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <span style={fieldLabel}>Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Engineer Spotlight — dark"
              autoFocus
              style={inputStyle}
            />
          </div>

          <div>
            <span style={fieldLabel}>Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional — what this template is for"
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 64, font: `400 13px/1.45 ${T.font}` }}
            />
          </div>

          <div>
            <span style={fieldLabel}>Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer', font: `500 13px/1.3 ${T.font}` }}
            >
              {TEMPLATE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 18px', borderTop: `1px solid ${T.divider}` }}>
          <button
            type="button"
            onClick={onClose}
            style={{ padding: '9px 16px', borderRadius: T.rMd, border: `1px solid ${T.border}`, background: 'transparent', color: T.fg2, cursor: 'pointer', font: `500 13px/1 ${T.font}` }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSave}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '9px 18px', borderRadius: T.rMd, border: 0,
              background: T.gradient, color: '#fff',
              cursor: canSave ? 'pointer' : 'default',
              font: `600 13px/1 ${T.font}`,
              boxShadow: canSave ? T.shadowBrand : 'none',
              opacity: canSave ? 1 : 0.5,
            }}
          >
            <LayoutTemplate size={15} />Save template
          </button>
        </div>
      </div>
    </div>
  );
}
